import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Package,
  Warehouse,
  ClipboardList,
  ClipboardCheck,
  Box,
  Settings,
  LogOut,
  Menu,
  X,
  FileText,
  Key,
  LayoutDashboard,
  Wrench,
  Tag,
  ScanLine,
  Users,
  ShieldAlert,
  KeyRound,
  BarChart3,
  UserCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ChangePasswordDialog from '@/components/ChangePasswordDialog';
import { hasPermission, hasAnyPermission, ROLE_LABELS } from '@/lib/auth';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_inventory-engine-2/artifacts/2d9zezs2_Zippy%20Logo%202.PNG';

export const DashboardLayout = ({ children, user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const location = useLocation();

  // Full navigation with permission gates. Items without `perm` are visible to all authenticated users.
  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, perm: 'dashboard.view' },
    { name: 'Analytics', href: '/analytics', icon: BarChart3, perm: 'dashboard.view' },
    {
      name: 'Customer Inventory',
      href: '/inventory',
      icon: Package,
      perm: 'customer_inventory.view',
      children: [
        { name: 'Products', href: '/inventory', perm: 'customer_inventory.view' },
        { name: 'Ledger', href: '/inventory/ledger', perm: 'ledger.view' },
      ],
    },
    {
      name: 'Internal Materials',
      href: '/internal-inventory',
      icon: Wrench,
      perm: 'internal_inventory.view',
      children: [
        { name: 'Materials', href: '/internal-inventory', perm: 'internal_inventory.view' },
        { name: 'Ledger', href: '/internal-inventory/ledger', perm: 'ledger.view' },
      ],
    },
    { name: 'Categories', href: '/categories', icon: Tag, perm: 'categories.view' },
    { name: 'Rack Assignment', href: '/rack-assignment', icon: ScanLine, perm: 'racks.assign' },
    { name: 'Warehouses', href: '/warehouses', icon: Warehouse, perm: 'warehouses.view' },
    { name: 'Pick Lists', href: '/pick-list', icon: ClipboardList, perm: 'orders.pick' },
    { name: 'Orders', href: '/orders', icon: Box, perm: 'orders.view' },
    { name: 'Cycle Counts', href: '/cycle-counts', icon: ClipboardCheck, perm: 'cycle_count.view' },
    { name: 'Users', href: '/users', icon: Users, perm: 'users.manage' },
    { name: 'Requesters', href: '/requesters', icon: UserCircle2, perm: 'requesters.view' },
    { name: 'Audit Trail', href: '/audit-logs', icon: ShieldAlert, perm: 'audit.view' },
    { name: 'Integrations', href: '/integrations', icon: Settings, perm: 'integrations.manage' },
    { name: 'API Settings', href: '/api-settings', icon: Key, perm: 'api_keys.manage' },
    { name: 'API Docs', href: '/api-docs', icon: FileText },
  ];

  const visibleNav = navigation
    .filter((item) => !item.perm || hasPermission(user, item.perm))
    .map((item) => {
      if (!item.children) return item;
      const kids = item.children.filter((c) => !c.perm || hasPermission(user, c.perm));
      // Hide parent if it exposes only a bare href already listed by visible children
      return { ...item, children: kids };
    })
    .filter((item) => {
      // If the item has a children array defined but none visible, hide it
      if (item.children && item.children.length === 0) return false;
      return true;
    });

  const renderNavItem = (item, onClickExtra = null) => {
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const parentActive = hasChildren
      ? item.children.some((c) => location.pathname === c.href)
      : location.pathname === item.href;

    return (
      <div key={item.name}>
        <Link
          to={item.href}
          onClick={onClickExtra || undefined}
          data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
          className={`
            group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-all duration-200
            ${
              parentActive && !hasChildren
                ? 'bg-[#1680e5] text-white'
                : hasChildren && parentActive
                ? 'bg-slate-800 text-white'
                : 'text-slate-300 hover:bg-slate-800 hover:text-white'
            }
          `}
        >
          <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
          {item.name}
        </Link>
        {hasChildren && parentActive && (
          <div className="ml-8 mt-1 space-y-1">
            {item.children.map((child) => {
              const childActive = location.pathname === child.href;
              return (
                <Link
                  key={child.href}
                  to={child.href}
                  onClick={onClickExtra || undefined}
                  data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}-${child.name.toLowerCase()}`}
                  className={`
                    block px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200
                    ${
                      childActive
                        ? 'bg-[#1680e5] text-white'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }
                  `}
                >
                  {child.name}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const userMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 w-full text-left focus:outline-none"
          data-testid="user-menu-trigger"
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name || 'User'}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email || ''}</p>
            <p className="text-[10px] text-[#00a89c] uppercase tracking-wider mt-0.5">
              {ROLE_LABELS[user?.role] || user?.role}
            </p>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={() => setChangePwOpen(true)} data-testid="menu-change-password">
          <KeyRound className="h-4 w-4 mr-2" />Change password
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout} data-testid="menu-logout" className="text-red-600 focus:text-red-600">
          <LogOut className="h-4 w-4 mr-2" />Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-slate-950">
          <div className="flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
            <div className="flex flex-shrink-0 items-center px-4 mb-8">
              <img src={LOGO_URL} alt="Zippy WMS" className="h-10 w-auto" />
              <span className="ml-3 text-xl font-bold text-white">Zippy WMS</span>
            </div>
            <nav className="mt-5 flex-1 space-y-1 px-2">
              {visibleNav.map((item) => renderNavItem(item))}
            </nav>
          </div>
          <div className="flex flex-shrink-0 border-t border-slate-800 p-4">
            <div className="flex items-center w-full gap-2">
              <div className="flex-1 min-w-0">{userMenu}</div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onLogout}
                data-testid="logout-button"
                className="text-slate-400 hover:text-white shrink-0"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden">
          <div className="fixed inset-0 z-40 flex">
            <div
              className="fixed inset-0 bg-slate-900 bg-opacity-75"
              onClick={() => setSidebarOpen(false)}
            />
            <div className="relative flex w-full max-w-xs flex-1 flex-col bg-slate-950">
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarOpen(false)}
                  className="text-white"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
              <div className="flex flex-shrink-0 items-center px-4 py-5">
                <img src={LOGO_URL} alt="Zippy WMS" className="h-10 w-auto" />
                <span className="ml-3 text-xl font-bold text-white">Zippy WMS</span>
              </div>
              <div className="mt-5 h-0 flex-1 overflow-y-auto">
                <nav className="space-y-1 px-2">
                  {visibleNav.map((item) => renderNavItem(item, () => setSidebarOpen(false)))}
                </nav>
              </div>
              {/* Mobile: user info + Change password + Sign out */}
              <div className="flex-shrink-0 border-t border-slate-800 p-4 space-y-2">
                <div className="text-white">
                  <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
                  <p className="text-xs text-slate-400 truncate">{user?.email || ''}</p>
                  <p className="text-[10px] text-[#00a89c] uppercase tracking-wider mt-0.5">
                    {ROLE_LABELS[user?.role] || user?.role}
                  </p>
                </div>
                <button
                  onClick={() => { setSidebarOpen(false); setChangePwOpen(true); }}
                  data-testid="mobile-change-password-btn"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-slate-300 hover:bg-slate-800 hover:text-white"
                >
                  <KeyRound className="h-4 w-4" />
                  Change password
                </button>
                <button
                  onClick={onLogout}
                  data-testid="mobile-logout-button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-red-600/10 text-red-300 hover:bg-red-600/20 hover:text-red-200 font-medium"
                >
                  <LogOut className="h-4 w-4" />
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        <div className="sticky top-0 z-10 flex h-16 flex-shrink-0 bg-white border-b border-slate-200 lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            className="px-4"
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </Button>
          <div className="flex flex-1 items-center justify-between px-4">
            <img src={LOGO_URL} alt="Zippy WMS" className="h-8 w-auto" />
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500 hidden sm:inline">{ROLE_LABELS[user?.role] || ''}</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={onLogout}
                data-testid="mobile-topbar-logout"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                title="Sign out"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
          </div>
        </main>
      </div>

      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
    </div>
  );
};

export default DashboardLayout;

// Suppress unused-import warning for hasAnyPermission (kept for callers)
export { hasAnyPermission };
